import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Suspense } from 'react';
import VKAuth from './components/VKAuth';
import './App.css';
import supabase from './supabaseClient';

// Lazy loading для всех компонентов
const Home = React.lazy(() => import('./components/Home'));
const SettingsPage = React.lazy(() => import('./components/SettingsPage'));
const SpecialistProfile = React.lazy(() => import('./components/SpecialistProfile'));
const SpecialistProfileTest = React.lazy(() => import('./components/SpecialistProfileTest'));
const NewRec = React.lazy(() => import('./components/NewRec'));
const Friends = React.lazy(() => import('./components/Friends'));
const AllSpecialists = React.lazy(() => import('./components/AllSpecialists'));
const AllUsers = React.lazy(() => import('./components/AllUsers'));
const Specialists = React.lazy(() => import('./components/Specialists'));
const FAQPage = React.lazy(() => import('./components/FAQPage'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./components/TermsOfUse'));
const AddStoriesForm = React.lazy(() => import('./components/AddStoriesForm'));
const VideoStoriesAdmin = React.lazy(() => import('./components/VideoStoriesAdmin'));
const SpecInvite = React.lazy(() => import('./components/SpecInvite'));
const Landing = React.lazy(() => import('./components/Landing'));

const NewRecRequest = React.lazy(() => import('./components/NewRecRequest'));
const EnhancedSpecialistRequests = React.lazy(() => import('./components/EnhancedSpecialistRequests'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const AdminAnalytics = React.lazy(() => import('./components/AdminAnalytics'));
const AdminDocumentation = React.lazy(() => import('./components/AdminDocumentation'));
const AdminFAQ = React.lazy(() => import('./components/AdminFAQ'));
const AdminPromoPage = React.lazy(() => import('./components/AdminPromoPage'));
const AdminLogs = React.lazy(() => import('./components/AdminLogs'));
const AdminInvites = React.lazy(() => import('./components/AdminInvites'));
const MyRecommendations = React.lazy(() => import('./components/MyRecommendations'));
const TempPage = React.lazy(() => import('./components/TempPage'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const Onboarding = React.lazy(() => import('./components/Onboarding'));
const BecomeSpecialist = React.lazy(() => import('./components/BecomeSpecialist'));

// Loading component
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '18px',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
  }}>
    Загрузка...
  </div>
);

function AuthGuard({ children, isAuthenticated, isLoading }) {
  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
}


function AuthScreen({ setIsAuthenticated, setUserEmail }) {
  const logo = '/logo.png';

  return (
    <div className="auth-screen">
      <div className="auth-content">
        <img src={logo} alt="Sarafine" className="auth-logo" />
        <p className="auth-subtitle">Войдите, чтобы продолжить</p>

        <div style={{ marginBottom: '24px' }}>
          <VKAuth
            onLoginSuccess={() => setIsAuthenticated(true)}
            onLoginError={(error) => console.error('Login error:', error)}
          />
        </div>

        <p className="auth-subsubtitle">
          Если вы открыли ссылку внутри мессенджера, то лучше перейдите в обычный браузер
        </p>

        <p className="auth-legal">
          Продолжая, вы принимаете <Link to="/terms-of-use">условия использования</Link>
          {' '}и <Link to="/privacy">политику конфиденциальности</Link>.
        </p>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Регистрация Service Worker (нужен для установки PWA)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker
      .register('/sw.js')
      .catch((error) => console.error('Service Worker registration failed:', error));
  }, []);

  // Проверка и поддержание сессии
  useEffect(() => {
    const handleAuthStateChange = async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setIsAuthenticated(true);
        setUserEmail(session?.user?.email || null);
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setUserEmail(null);
      }
    };

    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session) {
          const expiresSoon = session.expires_at - Math.floor(Date.now() / 1000) < 86400;
          if (expiresSoon) {
            const { data: { session: newSession }, error: refreshError } = 
              await supabase.auth.refreshSession();
            if (refreshError) throw refreshError;
            if (newSession) {
              setIsAuthenticated(true);
              setUserEmail(newSession.user.email);
              setAuthChecked(true);
              return;
            }
          }
          
          setIsAuthenticated(true);
          setUserEmail(session.user.email);
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setAuthChecked(true);
        setIsLoading(false);
      }
    };

    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);
    return () => subscription?.unsubscribe();
  }, []);

useEffect(() => {
  const tg = window.Telegram?.WebApp;
  
  if (tg) {
    tg.expand();
    tg.disableVerticalSwipes?.();
    
    return () => {
      tg.offEvent('viewportChanged');
    };
  }
}, []);

  // Обработка возврата из VK Auth
  const [vkCallbackHandled, setVkCallbackHandled] = useState(false);
  
  useEffect(() => {
    if (!isAuthenticated && authChecked && !vkCallbackHandled) {
      const handleVKCallback = async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();

          if (session) {
            setIsAuthenticated(true);
            setUserEmail(session.user.email);
            setVkCallbackHandled(true);
            // Очищаем URL от параметров VK, если они есть
            if (window.location.search.includes('code=') || window.location.hash.includes('code=')) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } else if (error) {
            console.error('VK callback error:', error);
            setVkCallbackHandled(true); // Stop retrying on error
          } else {
          }
        } catch (error) {
          console.error('VK callback exception:', error);
          setVkCallbackHandled(true);
        }
      };

      handleVKCallback();
    }
  }, [isAuthenticated, authChecked, vkCallbackHandled]);

  const isAdmin = userEmail === '172014705@vk.com';

  return (
    <Router>
      <Routes>
        {/* Публичные роуты — рендерятся сразу, без ожидания auth */}
        <Route
          path="/promo"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Landing />
            </Suspense>
          }
        />

        

        <Route
          path="/new"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Onboarding />
            </Suspense>
          }
        />

        <Route
          path="/become-specialist"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <BecomeSpecialist />
            </Suspense>
          }
        />

        <Route path="/faq" element={
          <Suspense fallback={<LoadingSpinner />}>
            <FAQPage />
          </Suspense>
        } />

        <Route path="/privacy" element={
          <Suspense fallback={<LoadingSpinner />}>
            <PrivacyPolicy />
          </Suspense>
        } />

        <Route path="/terms-of-use" element={
          <Suspense fallback={<LoadingSpinner />}>
            <TermsOfUse />
          </Suspense>
        } />

        <Route path="/newrec/:uniqueCode" element={
          <Suspense fallback={<LoadingSpinner />}>
            <NewRec />
          </Suspense>
        } />

        <Route path="/specinvite" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SpecInvite />
          </Suspense>
        } />

        <Route 
          path="/temppage" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <TempPage />
            </Suspense>
          } 
        />

        <Route 
          path="/reset-password" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ResetPassword />
            </Suspense>
          } 
        />

        <Route
          path="/"
          element={
            !authChecked ? (
              <LoadingSpinner />
            ) : isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <AuthScreen 
                setIsAuthenticated={setIsAuthenticated} 
                setUserEmail={setUserEmail} 
              />
            )
          }
        />

        {/* Защищённые роуты — с проверкой auth */}
        <Route path="/home" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <Home setIsAuthenticated={setIsAuthenticated} userEmail={userEmail} />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/friends" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <Friends />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/specialists/:categoryId" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <Specialists />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/specialist/:id" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialistProfile />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/specialist-test/:id" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialistProfileTest />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/new-rec-request" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <NewRecRequest />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/myrecs" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <MyRecommendations />
            </Suspense>
          </AuthGuard>
        } />
        <Route path="/settings" element={
          <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Suspense fallback={<LoadingSpinner />}>
              <SettingsPage />
            </Suspense>
          </AuthGuard>
        } />

        <Route 
          path="/admin" 
          element={
            <AuthGuard isAuthenticated={isAuthenticated} isLoading={isLoading}>
              {isAdmin ? (
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminPanel />
                </Suspense>
              ) : (
                <Navigate to="/home" replace />
              )}
            </AuthGuard>
          }
        >
          <Route path="analytics" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminAnalytics />
            </Suspense>
          } />
          <Route path="enhanced-requests" element={
            <Suspense fallback={<LoadingSpinner />}>
              <EnhancedSpecialistRequests />
            </Suspense>
          } />
          <Route path="logs" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminLogs />
            </Suspense>
          } />
          <Route path="add-stories" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AddStoriesForm />
            </Suspense>
          } />
          <Route path="video-stories" element={
            <Suspense fallback={<LoadingSpinner />}>
              <VideoStoriesAdmin />
            </Suspense>
          } />
          <Route path="all-specialists" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AllSpecialists />
            </Suspense>
          } />
          <Route path="all-users" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AllUsers />
            </Suspense>
          } />
          <Route path="faq" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminFAQ />
            </Suspense>
          } />
          <Route path="promo" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminPromoPage />
            </Suspense>
          } />
          <Route path="documentation" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminDocumentation />
            </Suspense>
          } />
          <Route path="invites" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminInvites />
            </Suspense>
          } />
        </Route>

        <Route path="*" element={<Navigate to={isAuthenticated ? "/home" : "/"} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
